import { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import Spinner from "../components/Spinner/Spinner";
import styles from "../styles/createService.module.css";

const COLOR_OPTIONS = [
  "#DE1468", "#3B82F6", "#10B981", "#8B5CF6",
  "#F59E0B", "#06B6D4", "#EC4899", "#EF4444",
];

const CreateService = ({ onNext, onBack, goToStep }) => {
  useEffect(() => {
    if (!localStorage.getItem("auth_token") && onBack) onBack();
  }, []);

  // Services list (generated + manually added, all local until "Continue")
  const [services, setServices] = useState(() => {
    // Initialize from localStorage on first render
    try {
      const raw = localStorage.getItem("generatedServices");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((s, i) => ({
            name: s.name,
            price: String(s.price),
            duration: String(s.duration),
            color: COLOR_OPTIONS[i % COLOR_OPTIONS.length],
          }));
        }
      }
    } catch (e) {
      console.log("Parse generatedServices error", e);
    }
    return [];
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Bottom-sheet state
  const [showSheet, setShowSheet] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [sheetPrice, setSheetPrice] = useState("");
  const [sheetDuration, setSheetDuration] = useState("");
  const [sheetColor, setSheetColor] = useState(COLOR_OPTIONS[0]);

  // Persist services to localStorage on every change (so back navigation doesn't lose data)
  useEffect(() => {
    if (services.length > 0) {
      localStorage.setItem(
        "generatedServices",
        JSON.stringify(
          services.map((s) => ({
            name: s.name,
            price: Number(s.price) || 0,
            duration: Number(s.duration) || 60,
            max: 1,
            type: "REGULAR",
          }))
        )
      );
    }
  }, [services]);

  // Remove service
  const removeService = (idx) => {
    setServices((prev) => prev.filter((_, i) => i !== idx));
  };

  // Open sheet for edit
  const openEdit = (idx) => {
    const svc = services[idx];
    setEditIdx(idx);
    setSheetName(svc.name);
    setSheetPrice(svc.price);
    setSheetDuration(svc.duration);
    setSheetColor(svc.color);
    setShowSheet(true);
  };

  // Open sheet for add
  const openAdd = () => {
    setEditIdx(null);
    setSheetName("");
    setSheetPrice("");
    setSheetDuration("");
    setSheetColor(COLOR_OPTIONS[0]);
    setShowSheet(true);
  };

  // Save from sheet
  const saveSheet = () => {
    if (!sheetName.trim()) return;
    const entry = {
      name: sheetName.trim(),
      price: sheetPrice || "0",
      duration: sheetDuration || "60",
      color: sheetColor,
    };
    if (editIdx !== null) {
      setServices((prev) => prev.map((s, i) => (i === editIdx ? entry : s)));
    } else {
      setServices((prev) => [...prev, entry]);
    }
    setShowSheet(false);
  };

  // Continue → create all services on backend
  const handleContinue = async () => {
    if (services.length === 0) return;

    const businessID = localStorage.getItem("business_id");
    const authToken = localStorage.getItem("auth_token");

    if (!authToken || !businessID) {
      setError("שגיאת התחברות — יש להתחבר מחדש");
      return;
    }

    setSaving(true);
    setError("");

    let allSuccess = true;
    for (const svc of services) {
      try {
        await axios.post(
          `${BASE_URL}/services/create`,
          {
            employeeId: businessID,
            type: "REGULAR",
            name: svc.name,
            description: "",
            time: Number(svc.duration) || 60,
            price: String(svc.price || "0"),
            maximumQueue: 1,
            isExactPrice: true,
            color: svc.color,
            businessApproval: false,
          },
          { headers: { Authorization: authToken } }
        );
      } catch (e) {
        // "שם השירות כבר קיים" (service name already taken) is the
        // backend's way of saying we already created this exact
        // service for this user on a previous pass. Most common cause:
        // user pressed Continue, then Back, then Continue again — the
        // services from the first attempt are still there. Treat as
        // success so the flow advances rather than dead-ending.
        const msg = e?.response?.data?.message || "";
        if (/כבר קיים|already exists|name.*taken/i.test(msg)) {
          console.log(`Service "${svc.name}" already exists — treating as success`);
          continue;
        }
        console.log("Create service error:", e);
        allSuccess = false;
      }
    }

    setSaving(false);

    if (allSuccess) {
      localStorage.removeItem("generatedServices");
      onNext();
    } else {
      setError("שגיאה ביצירת שירותים — בדקו את החיבור ונסו שוב");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <main className={styles.main} style={{ width: "100%", maxWidth: "560px" }}>
          <div className={styles.serviceSection}>
            <h2 className={styles.sectionTitle}>ניהול המחירון שלכם</h2>
            <p className={styles.sectionSubtitle}>
              הגדרה ראשונית - הכל ניתן לשינוי בהמשך 😊
            </p>

            {/* Add button — top */}
            <button className={styles.addBtn} onClick={openAdd}>
              + הוספה
            </button>

            {/* Service list */}
            <div className={styles.serviceList}>
              {services.map((svc, idx) => (
                <div key={idx} className={styles.svcCard}>
                  <div
                    className={styles.svcColorDot}
                    style={{ backgroundColor: svc.color }}
                  />
                  <div className={styles.svcInfo}>
                    <span className={styles.svcName}>{svc.name}</span>
                    <span className={styles.svcMeta}>
                      {svc.duration} דק׳ | ₪{svc.price}
                    </span>
                  </div>
                  <div className={styles.svcActions}>
                    <button
                      className={styles.svcEditBtn}
                      onClick={() => openEdit(idx)}
                    >
                      ✎
                    </button>
                    <button
                      className={styles.svcDeleteBtn}
                      onClick={() => removeService(idx)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Info note */}
            <div className={styles.infoNote}>
              <p>
                שימו לב, זוהי רק הגדרה בסיסית ראשונה - ניתן להוסיף הגדרות
                מתקדמות לתור (לשיעור, אם זה שיעור) בהמשך.
              </p>
            </div>

            {error && (
              <p style={{ color: "#EF4444", textAlign: "center", marginTop: "12px", fontSize: "14px" }}>
                {error}
              </p>
            )}
          </div>
        </main>
      </div>

      {/* Footer */}
      <div className={styles.stickyFooter}>
        <div className={styles.footerInner}>
          <button
            className={styles.continueBtn}
            onClick={handleContinue}
            disabled={services.length === 0 || saving}
          >
            {saving ? <Spinner /> : "המשך"}
          </button>
        </div>
      </div>

      {/* Bottom Sheet */}
      {showSheet && (
        <div className={styles.sheetBackdrop} onClick={() => setShowSheet(false)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sheetHeader}>
              <span className={styles.sheetTitle}>
                {editIdx !== null ? "עריכת שירות" : "הוספת תור למחירון"}
              </span>
              <button
                className={styles.sheetClose}
                onClick={() => setShowSheet(false)}
              >
                ✕
              </button>
            </div>

            {/* Name */}
            <label className={styles.sheetLabel}>שם התור</label>
            <input
              className={styles.sheetInput}
              type="text"
              placeholder="לדוגמה: תספורת, עיסוי..."
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
            />

            {/* Price + Duration row */}
            <div className={styles.sheetRow}>
              <div className={styles.sheetFieldHalf}>
                <label className={styles.sheetLabel}>מחיר</label>
                <div className={styles.sheetInputWrap}>
                  <span className={styles.sheetCurrency}>₪</span>
                  <input
                    className={styles.sheetInput}
                    type="number"
                    placeholder="0"
                    value={sheetPrice}
                    onChange={(e) => setSheetPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.sheetFieldHalf}>
                <label className={styles.sheetLabel}>משך (דקות)</label>
                <input
                  className={styles.sheetInput}
                  type="number"
                  placeholder="לדוגמה: 60"
                  value={sheetDuration}
                  onChange={(e) => setSheetDuration(e.target.value)}
                />
              </div>
            </div>

            {/* Color picker */}
            <label className={styles.sheetLabel}>צבע ביומן</label>
            <div className={styles.colorRow}>
              {COLOR_OPTIONS.map((c) => (
                <div
                  key={c}
                  className={`${styles.colorCircle} ${
                    sheetColor === c ? styles.colorCircleActive : ""
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setSheetColor(c)}
                >
                  {sheetColor === c && <span className={styles.colorCheck}>✓</span>}
                </div>
              ))}
            </div>

            {/* Sheet info */}
            <div className={styles.infoNote}>
              <p>
                שימו לב, זוהי רק הגדרה בסיסית ראשונה - ניתן להוסיף הגדרות
                מתקדמות לתור (לשיעור, אם זה שיעור) בהמשך.
              </p>
            </div>

            {/* Sheet footer */}
            <div className={styles.sheetFooter}>
              <button
                className={styles.sheetSaveBtn}
                onClick={saveSheet}
                disabled={!sheetName.trim()}
              >
                {editIdx !== null ? "שמור" : "הוספה"}
              </button>
              <button
                className={styles.sheetCancelBtn}
                onClick={() => setShowSheet(false)}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateService;
